import { useCallback, useMemo, useState } from "react";
import { useCostAdjustments, useSaveCostAdjustmentsMutation } from "../../../api_utils/requests";
import type { CostAdjustment, CostAdjustmentEditable } from "../../../api_utils/types";
import { assertNumber } from "../../../helper_functions/utils";

export function useEditableCostAdjustments(purchaseOrderId: number): {
  costAdjustments: CostAdjustmentEditable[];
  setCostAdjustments: (costAdjustments: CostAdjustmentEditable[]) => void;
  total: number;
  isLoading: boolean;
  isSaving: boolean;
  save: () => Promise<void>;
  discard: () => void;
} {
  const { data, isPlaceholderData } = useCostAdjustments(purchaseOrderId);
  const { mutateAsync, isPending } = useSaveCostAdjustmentsMutation();
  const [editedCostAdjustments, setEditedCostAdjustments] = useState<CostAdjustmentEditable[] | null>(null);
  const savedCostAdjustments = useMemo(() => {
    const costAdjustments = sortCostAdjustments(formatClientSide(data.cost_adjustments));
    if (!costAdjustments.some((costAdjustment) => costAdjustment.label === "Tax")) {
      costAdjustments.unshift({ label: "Tax", amount: "", purchase_order_id: purchaseOrderId });
    }
    return costAdjustments;
  }, [data, purchaseOrderId]);
  const costAdjustments = editedCostAdjustments ?? savedCostAdjustments;
  const total = costAdjustments.reduce(
    (sum, costAdjustment) => sum + (costAdjustment._destroy ? 0 : formatAmount(costAdjustment)),
    0,
  );

  const save = useCallback(async (): Promise<void> => {
    await mutateAsync({
      purchase_order_id: purchaseOrderId,
      cost_adjustments: costAdjustments.map((costAdjustment) => ({
        ...costAdjustment,
        amount: formatAmount(costAdjustment),
      })),
    });
    setEditedCostAdjustments(null);
  }, [costAdjustments, mutateAsync, purchaseOrderId]);

  const discard = useCallback((): void => {
    setEditedCostAdjustments(null);
  }, []);

  return {
    costAdjustments,
    setCostAdjustments: setEditedCostAdjustments,
    total,
    isLoading: isPlaceholderData,
    isSaving: isPending,
    save,
    discard,
  };
}

const formatAmount = (costAdjustment: CostAdjustmentEditable): number => {
  const amountNumber = assertNumber(costAdjustment.amount);
  return costAdjustment.label === "Discount" ? -Math.abs(amountNumber) : amountNumber;
};

const formatClientSide = (costAdjustments: CostAdjustment[]): CostAdjustmentEditable[] =>
  costAdjustments.map((costAdjustment) => ({
    ...costAdjustment,
    amount: costAdjustment.amount.toString(),
  }));

const sortCostAdjustments = (costAdjustments: CostAdjustmentEditable[]): CostAdjustmentEditable[] =>
  costAdjustments.sort((leftCostAdjustment, rightCostAdjustment) => {
    if (leftCostAdjustment.label === "Tax") return -1;
    if (rightCostAdjustment.label === "Tax") return 1;
    return leftCostAdjustment.id - rightCostAdjustment.id;
  });
